import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MenuItem, menuItems } from './menu.config';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map, Observable, Subscription } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { AsyncPipe, NgClass, NgOptimizedImage } from '@angular/common';
import { UserService } from '../../services/user.service';
import { User } from '../../models/user';
import { MsalService } from '@azure/msal-angular';
import { UrlPerEnvPipe } from './pipes/url-per-env.pipe';
import { environment } from '../../../environments/environment';
import { EllipsisTooltipDirective } from '../../directives/ellipsis-tooltip.directive';
import { AvatarComponent } from '../../components/avatar/avatar.component';

type MenuState = 'expanded' | 'collapsed';

// Fixed cap for the tray's expand/collapse max-height transition — needs to be at least the
// tray's real content height (children * row height + tray padding) or the animation looks
// like it finishes early, but doesn't need to match it exactly (see menu.component.scss).
const CHILD_ROW_HEIGHT = 60;
const TRAY_PADDING = 16;
// Long enough to cross the gap from the icon into the flyout without it vanishing mid-move;
// short enough that lingering after the mouse leaves doesn't feel sticky.
const FLYOUT_CLOSE_DELAY_MS = 200;

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    NgOptimizedImage,
    NgClass,
    AsyncPipe,
    UrlPerEnvPipe,
    EllipsisTooltipDirective,
    AvatarComponent
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss'
})
export class MenuComponent implements OnInit, OnDestroy {
  availableModules$: Observable<string[]> | null = null;
  userInfo$: Observable<User> | null = null;
  menuState: MenuState = (localStorage.getItem('menuState') as MenuState) || 'expanded';
  readonly menuItems: MenuItem[] = menuItems;
  // Single-open accordion: at most one group is expanded at a time, so opening one collapses
  // whichever other group (e.g. Administration) was previously open.
  expandedGroup: string | null = null;

  private subscription = new Subscription();
  private flyoutCloseTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private userService: UserService,
    private msalService: MsalService,
    private router: Router,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit() {
    this.availableModules$ = this.userService.getUserInfo().pipe(map(({ modules }) => modules));
    this.userInfo$ = this.userService.getUserInfo();
    this.menuState = (localStorage.getItem('menuState') as MenuState) || 'expanded';
    const activeParent = this.menuItems.find(item =>
      item.children?.some(child => child.path && this.router.url.startsWith(`/${child.path}`))
    );
    this.expandedGroup = activeParent?.label ?? null;

    // The expanded/collapsed preference above is a desktop-only concept persisted across
    // sessions — on a handset viewport it can leave the full 229px (or even the 85px collapsed
    // rail) eating most of the screen, squeezing whatever page content sits beside it (e.g. the
    // Alerts admin mobile tab strip) into an unreadable, overlapping sliver on first load. Force
    // the icon-only rail whenever the viewport becomes mobile-sized, without touching the saved
    // desktop preference — toggleMenu()'s own localStorage write is untouched, so returning to
    // desktop width still restores whatever the user last chose there.
    this.subscription.add(
      this.breakpointObserver.observe(Breakpoints.Handset).subscribe(result => {
        if (result.matches) {
          this.menuState = 'collapsed';
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.cancelFlyoutClose();
  }

  toggleMenu() {
    this.menuState = this.menuState === 'expanded' ? 'collapsed' : 'expanded';
    localStorage.setItem('menuState', this.menuState);
  }

  toggleSubmenu(item: MenuItem, event: Event) {
    event.stopPropagation();
    if (this.menuState === 'collapsed') {
      // Collapsed rail interacts with a group via the flyout instead of the inline tray.
      return;
    }
    this.expandedGroup = this.expandedGroup === item.label ? null : item.label;
  }

  isExpanded(item: MenuItem): boolean {
    return this.menuState !== 'collapsed' && this.expandedGroup === item.label;
  }

  openFlyout(trigger: MatMenuTrigger): void {
    if (this.menuState !== 'collapsed') {
      return;
    }
    this.cancelFlyoutClose();
    trigger.openMenu();
  }

  scheduleFlyoutClose(trigger: MatMenuTrigger): void {
    this.cancelFlyoutClose();
    this.flyoutCloseTimeoutId = setTimeout(() => {
      trigger.closeMenu();
      this.flyoutCloseTimeoutId = null;
    }, FLYOUT_CLOSE_DELAY_MS);
  }

  cancelFlyoutClose(): void {
    if (this.flyoutCloseTimeoutId !== null) {
      clearTimeout(this.flyoutCloseTimeoutId);
      this.flyoutCloseTimeoutId = null;
    }
  }

  // A group with children (Scan Suite) may have no single claim of its own — each child is
  // gated independently, so the group itself is visible whenever at least one child is.
  isItemVisible(item: MenuItem, availableModules: string[]): boolean {
    if (item.children?.length) {
      return item.children.some(child => availableModules.includes(child.claimName));
    }
    return availableModules.includes(item.claimName);
  }

  visibleChildren(item: MenuItem, availableModules: string[]): MenuItem[] {
    return (item.children ?? []).filter(child => availableModules.includes(child.claimName));
  }

  childTrayMaxHeight(item: MenuItem): number {
    return (item.children?.length ?? 0) * CHILD_ROW_HEIGHT + TRAY_PADDING;
  }

  logout() {
    const account = this.msalService.instance.getActiveAccount();
    this.msalService.logoutRedirect({
      account,
      postLogoutRedirectUri: environment.msal.postLogoutUrl
    });
  }
}
